import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouterState } from "@tanstack/react-router";
import type { Aggregates, DataSourceInfo, ExporterPerspective, Lead } from "@/lib/trade-types";

interface DataContextValue {
  leads: Lead[] | undefined;
  aggregates: Aggregates | undefined;
  source: DataSourceInfo;
  isLoading: boolean;
  error: string | null;
  perspective: ExporterPerspective;
  setPerspective: (perspective: ExporterPerspective) => void;
}

const DEFAULT_SOURCE: DataSourceInfo = {
  kind: "bundled",
  label: "HS 2022 buyer-leads intelligence",
  importedAt: null,
  fileName: null,
  rows: 0,
};

const DataContext = createContext<DataContextValue | null>(null);

async function loadCoreData(): Promise<{
  aggregates: Aggregates;
  source: DataSourceInfo;
}> {
  const [aggregatesResponse, manifestResponse] = await Promise.all([
    fetch("/data/aggregates.json"),
    fetch("/data/manifest.json", { cache: "no-cache" }).catch(() => null),
  ]);

  if (!aggregatesResponse.ok) {
    throw new Error("The dashboard dataset could not be loaded. Rebuild public/data and redeploy.");
  }

  const aggregates = (await aggregatesResponse.json()) as Aggregates;
  let manifest: { generated_at?: string; source_file?: string; rows?: number } = {};
  if (manifestResponse?.ok) manifest = await manifestResponse.json();

  return {
    aggregates,
    source: {
      ...DEFAULT_SOURCE,
      importedAt: manifest.generated_at ?? null,
      fileName: manifest.source_file ?? null,
      rows: manifest.rows ?? aggregates.meta.total,
    },
  };
}

async function loadLeadData(): Promise<Lead[]> {
  const response = await fetch("/data/leads.json");
  if (!response.ok) throw new Error("Buyer intelligence could not be loaded.");
  return (await response.json()) as Lead[];
}

const LEAD_DATA_ROUTES = new Set([
  "/",
  "/eu",
  "/gsp-plus",
  "/hs",
  "/leads",
  "/logistics",
  "/markets",
  "/opportunities",
  "/payments",
]);

function savedPerspective(): ExporterPerspective {
  if (typeof window === "undefined") return "UZB";
  const url = new URL(window.location.href);
  const query = url.searchParams.get("perspective")?.toUpperCase();
  if (query === "SVK" || query === "UZB") return query;
  return window.localStorage.getItem("opportunity-navigator-perspective")?.toUpperCase() === "SVK"
    ? "SVK"
    : "UZB";
}

export function DataProvider({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const needsLeads = LEAD_DATA_ROUTES.has(pathname);
  const [leads, setLeads] = useState<Lead[]>();
  const [aggregates, setAggregates] = useState<Aggregates>();
  const [source, setSource] = useState<DataSourceInfo>(DEFAULT_SOURCE);
  const [coreLoading, setCoreLoading] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [perspective, setPerspectiveState] = useState<ExporterPerspective>("UZB");

  const applyCoreData = useCallback((dataset: Awaited<ReturnType<typeof loadCoreData>>) => {
    setAggregates(dataset.aggregates);
    setSource(dataset.source);
  }, []);

  useEffect(() => {
    setPerspectiveState(savedPerspective());
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const dataset = await loadCoreData();
        if (active) applyCoreData(dataset);
      } catch (caught) {
        if (active)
          setError(caught instanceof Error ? caught.message : "The dataset could not be loaded.");
      } finally {
        if (active) setCoreLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [applyCoreData]);

  useEffect(() => {
    if (!needsLeads || leads) return;
    let active = true;
    setLeadsLoading(true);
    void loadLeadData()
      .then((data) => {
        if (active) setLeads(data);
      })
      .catch((caught: unknown) => {
        if (active)
          setError(caught instanceof Error ? caught.message : "Buyer intelligence could not load.");
      })
      .finally(() => {
        if (active) setLeadsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [leads, needsLeads]);

  const setPerspective = useCallback((next: ExporterPerspective) => {
    setPerspectiveState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("opportunity-navigator-perspective", next);
      const url = new URL(window.location.href);
      url.searchParams.set("perspective", next);
      window.history.replaceState({}, "", url);
    }
  }, []);

  const isLoading = coreLoading || (needsLeads && leadsLoading);
  const value = useMemo<DataContextValue>(
    () => ({ leads, aggregates, source, isLoading, error, perspective, setPerspective }),
    [aggregates, error, isLoading, leads, perspective, setPerspective, source],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useTradeData(): DataContextValue {
  const value = useContext(DataContext);
  if (!value) throw new Error("useTradeData must be used inside DataProvider.");
  return value;
}

export function useAggregates() {
  const { aggregates, isLoading, error } = useTradeData();
  return { data: aggregates, isLoading, error };
}

export function useLeads() {
  const { leads, isLoading, error } = useTradeData();
  return { data: leads, isLoading, error };
}

export function usePerspective() {
  const { perspective, setPerspective } = useTradeData();
  return { perspective, setPerspective };
}
